import Web3 from "web3";
import stakingPools from "@/domain/limitedSaunaStaking/pools";
import token from "@/domain/limitedSaunaStaking/token";
import rewardToken from "@/domain/limitedSaunaStaking/rewardToken";
import network from "@/domain/limitedSaunaStaking/network";
import { MAX_UINT256, METAMASK, PRODUCTION } from "@/utils/constants";
import numFormat from "number-format.js";
import {
  format,
  fromUnixTime,
  secondsToMinutes,
  secondsToHours,
  addSeconds,
  getUnixTime,
} from "date-fns";
import { ssctPerBnb, saunaPerBnb } from "../tokens";
import pancakePair from "@/domain/limitedSaunaStaking/pairs/pancakePair";

export const getAccountData = async (web3, token, accountAddresses = []) => {
  try {
    let accounts =
      accountAddresses.length > 0
        ? accountAddresses
        : sessionStorage.getItem("provider") == METAMASK
        ? await _getAccountForMetaMask()
        : await web3.eth.getAccounts();

    let address = accounts[0];

    address = web3.utils.toChecksumAddress(address);
    let contract = new web3.eth.Contract(token.abi, token.contractAddress);

    let balance = await contract.methods
      .balanceOf(address)
      .call({ from: address });

    balance = web3.utils.toBN(balance).toString();

    let symbol = await contract.methods.symbol().call();

    return {
      address,
      balance,
      symbol,
    };
  } catch (e) {
    console.error(e);
    return null;
  }
};

export const getPools = async (web3, accountAddress) => {
  try {
    let tokenContract = new web3.eth.Contract(token.abi, token.contractAddress);

    let tokenSymbol = await tokenContract.methods.symbol().call();
    let tokenDecimals = await tokenContract.methods.decimals().call();

    let pools = await Promise.all(
      stakingPools.map(async (stakingPool, index) => {
        let poolContract = new web3.eth.Contract(
          stakingPool.abi,
          stakingPool.contractAddress
        );

        let minDepositAmount = await poolContract.methods
          .minDepositAmount()
          .call();

        let totalStakingTokens = await poolContract.methods
          .totalStakings()
          .call();

        let stakeTokenAddress = await poolContract.methods.stakedToken().call();
        let rewardTokenAddress = await poolContract.methods
          .rewardToken()
          .call();

        let rewardTokenContract = new web3.eth.Contract(
          rewardToken.abi,
          rewardTokenAddress
        );

        let rewardTokenDecimals = await rewardTokenContract.methods
          .decimals()
          .call();

        let rewardTokenSymbol = await rewardTokenContract.methods
          .symbol()
          .call();

        let rewardPerBlock = await poolContract.methods.rewardPerBlock().call();

        let apr =
          totalStakingTokens > 0
            ? _calculateApr(rewardPerBlock, totalStakingTokens)
            : stakingPool.minApr;

        let isActive = await poolContract.methods.isInitialized().call();

        let isFrozen = await poolContract.methods.isFrozen().call();

        let compoundCallFee = await poolContract.methods
          .compoundCallFee()
          .call();

        compoundCallFee = compoundCallFee / 100;

        let totalPendingCakeRewards = await poolContract.methods
          .calculateTotalPendingCakeRewards()
          .call();

        totalPendingCakeRewards = _convertWeiToEther(
          web3,
          totalPendingCakeRewards
        );

        let compoundWithdrawFeePeriod = await poolContract.methods
          .compoundWithdrawFeePeriod()
          .call();

        let compoundWithdrawFeePeriodString = null;

        if (compoundWithdrawFeePeriod >= 3600) {
          let hours = secondsToHours(compoundWithdrawFeePeriod);
          compoundWithdrawFeePeriodString = `${hours} hour(s)`;

          if (hours >= 24) {
            let days = Math.round(hours / 24);
            compoundWithdrawFeePeriodString = `${days} day(s)`;
          }
        }

        if (compoundWithdrawFeePeriod < 3600) {
          let minutes = secondsToMinutes(compoundWithdrawFeePeriod);
          compoundWithdrawFeePeriodString = `${minutes} minute(s)`;
        }

        let account = {
          earned: `${0}`,
          staked: `${0}`,
          isApproved: false,
          harvestableTime: new Date().getTime() / 1000,
          withdrawableTime: new Date().getTime() / 1000,
          registered: false,
          rewardLockedup: 0,
          earnedDescription: null,
          totalClaimableTokens: `${0}`,
          totalClaimableUsd: `${0}`,
          compoundStaked: `${0}`,
        };

        let totalShares = 0;

        if (accountAddress) {
          let accountPoolData = await poolContract.methods
            .userInfo(accountAddress)
            .call({ from: accountAddress });

          totalShares = await poolContract.methods.totalShares().call();

          let earnedFromLastAction = 0;
          let amount = accountPoolData.amount;

          if (totalShares > 0) {
            let totalEarned = web3.utils
              .toBN(amount)
              .mul(web3.utils.toBN(totalStakingTokens))
              .div(web3.utils.toBN(totalShares))
              .toString();

            let saunaAtLastUserAction = accountPoolData.saunaAtLastUserAction;

            earnedFromLastAction = web3.utils
              .toBN(totalEarned)
              .sub(web3.utils.toBN(saunaAtLastUserAction))
              .toString();

            let tokenPerBnb =
              process.env.NODE_ENV == PRODUCTION
                ? await saunaPerBnb(web3)
                : await ssctPerBnb(web3);

            let pancakePairContract = new web3.eth.Contract(
              pancakePair.abi,
              pancakePair.contractAddress
            );

            let reserves = await pancakePairContract.methods
              .getReserves()
              .call();

            let bnbPrice = reserves[1] / reserves[0];

            let earnedInUsd =
              _convertWeiToEther(web3, earnedFromLastAction) *
              (bnbPrice / tokenPerBnb);

            let lastUserActionTime = format(
              fromUnixTime(accountPoolData.lastUserActionTime),
              "M/dd/yyyy, hh:mm:ss aa"
            );

            account.earnedDescription = `
              <p>
              ${numFormat(
                "#,##0.00000",
                _convertWeiToEther(web3, earnedFromLastAction)
              )} ${numFormat("#,##0.00", token.name)}
              </p>
              <p>
              ~$ ${numFormat("#,##0.00", earnedInUsd)}
              </p>
              <p>
              Earned since your last action ${lastUserActionTime}
              </p>
              `;

            account.totalClaimableTokens = await poolContract.methods
              .calculateClaimRewards()
              .call({ from: accountAddress });

            account.totalClaimableTokens = _convertWeiToEther(
              web3,
              account.totalClaimableTokens
            );

            account.totalClaimableUsd =
              account.totalClaimableTokens * (bnbPrice / tokenPerBnb);
          }

          account.registered = accountPoolData.registered;
          account.rewardLockedup = accountPoolData.rewardLockedup;

          if (accountPoolData.registered) {
            account.isApproved = true;

            if (totalShares == 0) {
              account.compoundStaked = web3.utils.toBN(amount).toString();
            } else {
              account.compoundStaked = web3.utils
                .toBN(amount)
                .mul(web3.utils.toBN(totalStakingTokens))
                .div(web3.utils.toBN(totalShares))
                .toString();
            }

            account.staked = amount;
            account.earned = earnedFromLastAction;
          }

          if (!account.isApproved) {
            let allowance = await tokenContract.methods
              .allowance(accountAddress, stakingPool.contractAddress)
              .call({ from: accountAddress });

            account.isApproved = allowance != 0;
          }

          if (account.staked !== 0) {
            account.harvestableTime = accountPoolData.nextHarvestUntil;

            if (accountPoolData.lastDepositedTime > 0) {
              account.withdrawableTime = addSeconds(
                fromUnixTime(accountPoolData.lastDepositedTime),
                compoundWithdrawFeePeriod
              );
            }

            if (accountPoolData.lastDepositedTime == 0) {
              account.withdrawableTime = addSeconds(
                new Date(),
                compoundWithdrawFeePeriod
              );
            }
          }
        }

        account.withdrawableTime = getUnixTime(account.withdrawableTime);

        let withdrawConstrains = {
          lockType: 2,
          unlockFeeType: 4,
          unlockFee: `${0}`,
          withdrawInterval: 0,
        };

        let compoundWithdrawFee = await poolContract.methods
          .compoundWithdrawFee()
          .call();

        if (compoundWithdrawFee > 0) {
          withdrawConstrains.unlockFee = compoundWithdrawFee / 100;
        }

        withdrawConstrains.withdrawInterval = compoundWithdrawFeePeriod * 1000;

        let harvestConstrains = {
          isLocked: false,
        };

        harvestConstrains.isLocked = await poolContract.methods
          .harvestLockOn()
          .call();

        return {
          title: stakingPool.title,
          description:
            minDepositAmount > 0
              ? `Min. Stake ${numFormat(
                  "#,##0.",
                  _convertWeiToEther(web3, minDepositAmount)
                )} ${tokenSymbol}`
              : `No minimum`,
          url: stakingPool.url,
          NETWORK: network.name,
          CHAIN_ID: network.chainIds[0],
          coinSymbol: tokenSymbol,
          mainCoinImage: null,
          smallerCoinImage: null,
          minDepositAmount: web3.utils.toBN(minDepositAmount).toString(),
          totalStakingTokens: web3.utils.toBN(totalStakingTokens).toString(),
          stakeTokenAddress: stakeTokenAddress,
          stakeTokenSymbol: tokenSymbol,
          stakeTokenDecimals: tokenDecimals,
          rewardTokenAddress: rewardTokenAddress,
          rewardTokenSymbol: rewardTokenSymbol,
          rewardTokenDecimals: rewardTokenDecimals,
          stakeContractAddress: stakingPool.contractAddress,
          status: isActive ? "ACTIVE" : "FINISHED",
          withdrawConstrains: withdrawConstrains,
          harvestConstrains: harvestConstrains,
          apr: apr,
          order: index + 1,
          isMaster: false,
          id: index + 1,
          createdAt: new Date(),
          updatedAt: new Date(),
          isFrozen: isFrozen,
          isFinished: isFrozen,
          account: account,
          compoundCallFee: compoundCallFee,
          totalPendingCakeRewards: totalPendingCakeRewards,
          compoundWithdrawFeePeriodString: compoundWithdrawFeePeriodString,
          totalShares: web3.utils.toBN(totalShares).toString(),
        };
      })
    );
    return pools;
  } catch (e) {
    console.error(e);
  }
};

export const approve = async (web3, stakingPool, accountAddress) => {
  try {
    let tokenContract = new web3.eth.Contract(token.abi, token.contractAddress);

    let approve = await tokenContract.methods
      .approve(stakingPool.stakeContractAddress, MAX_UINT256)
      .send({ from: accountAddress });

    return approve;
  } catch (e) {
    console.error(e);
    return null;
  }
};

export const deposit = async (
  web3,
  stakingPool,
  accountAddress,
  depositAmount
) => {
  try {
    let amount = _convertEtherToWei(web3, depositAmount);

    let abi = stakingPools.find(
      (pool) => pool.contractAddress == stakingPool.stakeContractAddress
    ).abi;
    let contract = new web3.eth.Contract(abi, stakingPool.stakeContractAddress);

    let checkApproval = _checkApproval(
      web3,
      accountAddress,
      stakingPool.stakeContractAddress
    );

    if (checkApproval) {
      let desposit = await contract.methods
        .deposit(amount)
        .send({ from: accountAddress });

      return desposit;
    }
    return null;
  } catch (e) {
    console.error(e);
    return null;
  }
};

export const withdraw = async (
  web3,
  stakingPool,
  accountAddress,
  withdrawAmount
) => {
  try {
    let amount = _convertEtherToWei(web3, withdrawAmount);

    let abi = stakingPools.find(
      (pool) => pool.contractAddress == stakingPool.stakeContractAddress
    ).abi;

    let contract = new web3.eth.Contract(abi, stakingPool.stakeContractAddress);

    let withdraw = await contract.methods
      .withdraw(amount)
      .send({ from: accountAddress });

    return withdraw;
  } catch (e) {
    console.error(e);
    return null;
  }
};

export const harvest = async (web3, stakingPool, accountAddress) => {
  try {
    let amount = _convertEtherToWei(web3, "0");

    let abi = stakingPools.find(
      (pool) => pool.contractAddress == stakingPool.stakeContractAddress
    ).abi;
    let contract = new web3.eth.Contract(abi, stakingPool.stakeContractAddress);

    let harvest = await contract.methods
      .withdraw(amount)
      .send({ from: accountAddress });

    return harvest;
  } catch (e) {
    console.error(e);
    return null;
  }
};

export const addToMetamask = async (web3) => {
  let tokenContract = new web3.eth.Contract(token.abi, token.contractAddress);

  let symbol = await tokenContract.methods.symbol().call();
  let decimals = await tokenContract.methods.decimals().call();
  let image = null;

  try {
    const isAdded = await window.ethereum.request({
      method: "wallet_watchAsset",
      params: {
        type: "ERC20",
        options: {
          address: token.contractAddress,
          symbol: symbol,
          decimals: decimals,
          image: image,
        },
      },
    });

    return isAdded;
  } catch (e) {
    console.error(e);
  }
};

export const _getWeb3Provider = () => {
  try {
    const web3 = new Web3(Web3.givenProvider || "ws://localhost:8545");

    if (!network.chainIds.includes(web3.eth.currentProvider.chainId)) {
      web3.eth.setProvider(network.rpcUrls[0]);
      return web3;
    }
    return web3;
  } catch (e) {
    console.error(e);
  }
};

export const _getContractInstance = (abi, address) => {
  let web3 = _getWeb3Provider();
  return new web3.eth.Contract(abi, address);
};

export const _getAccountForMetaMask = async () => {
  if (!window.ethereum) return;

  let accounts = [];
  await window.ethereum
    .request({
      method: "eth_requestAccounts",
    })
    .then(async (acts) => {
      accounts = acts;
    })
    .catch((error) => {
      if (error.code === 4001) {
        console.log("Permissions needed to continue.");
      } else {
        console.log(error);
      }
      accounts = [];
    });

  return accounts;
};

export const _checkApproval = async (web3, accountAddress, approveAddress) => {
  let tokenContract = new web3.eth.Contract(token.abi, token.contractAddress);

  let allowance = await tokenContract.methods
    .allowance(accountAddress, approveAddress)
    .call({ from: accountAddress });

  if (allowance != 0) {
    return true;
  }

  let isApproved = await approve(tokenContract, approveAddress, accountAddress);

  if (isApproved != null) {
    return true;
  }

  return false;
};

export const _toCurrency = (num) => new Intl.NumberFormat("en-US").format(num);

export const _convertWeiToEther = (web3, amount) =>
  web3.utils.fromWei(amount, "ether");

export const _convertEtherToWei = (web3, amount) =>
  web3.utils.toWei(amount, "ether");

export const _convertWeiToGwei = (web3, amount) =>
  web3.utils.fromWei(amount, "gwei");

export const _calculateApr = (rewardPerBlock, totalStakingTokens) => {
  return ((rewardPerBlock * 28800) / totalStakingTokens) * 100 * 365;
};
